<script setup>
import { computed } from "vue";
import { BRAND_LOGO_URL } from "@/shared/config/constants";
import { cacheTokens, compact, fmt, padRank, usageDetailLine, usd } from "@/shared/utils/format";
import MemberBadges from "@/features/leaderboard/components/MemberBadges.vue";

const props = defineProps({
  items: { type: Array, default: () => [] },
  user: { type: Object, default: null },
  date: { type: String, required: true },
  mode: { type: String, default: "day" },
  emptyText: { type: String, default: "这一天还没有上传记录。" },
});

const isTotalMode = computed(() => props.mode === "total");
const isAggregateMode = computed(() => props.mode !== "day");
const totalHeader = computed(() => (isTotalMode.value ? "已上传总量" : "窗口总量"));
</script>

<template>
  <div v-if="!items.length" class="board-empty">
    <img :src="BRAND_LOGO_URL" alt="" />
    <p>{{ emptyText }}</p>
  </div>

  <template v-else>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>排名</th>
            <th>用户</th>
            <th>{{ isAggregateMode ? totalHeader : "total tokens" }}</th>
            <th>{{ isAggregateMode ? "活跃天数" : "cache" }}</th>
            <th>{{ isAggregateMode ? "最近日期" : "input / output" }}</th>
            <th>cost</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id" :class="{ mine: item.user?.id === user?.id }">
            <td class="rank">{{ padRank(item.rank) }}</td>
            <td class="user">
              <div class="user-cell">
                <a
                  v-if="item.user?.profileUrl"
                  class="profile-avatar-link"
                  :href="item.user.profileUrl"
                  :aria-label="`查看 ${item.user?.displayName || '小能熊用户'} 的社区主页`"
                >
                  <img v-if="item.user?.avatarUrl" :src="item.user.avatarUrl" alt="" />
                  <span v-else class="avatar-fallback">{{ (item.user?.displayName || "小").slice(0, 1) }}</span>
                </a>
                <template v-else>
                  <img v-if="item.user?.avatarUrl" :src="item.user.avatarUrl" alt="" />
                  <span v-else class="avatar-fallback">{{ (item.user?.displayName || "小").slice(0, 1) }}</span>
                </template>
                <div class="user-meta">
                  <div class="user-name-line">
                    <a v-if="item.user?.profileUrl" class="profile-name-link" :href="item.user.profileUrl">
                      <strong>{{ item.user?.displayName || "小能熊用户" }}</strong>
                    </a>
                    <strong v-else>{{ item.user?.displayName || "小能熊用户" }}</strong>
                    <span v-if="item.user?.id === user?.id" class="me-pill">我</span>
                  </div>
                  <MemberBadges :memberships="item.user?.memberships" compact />
                  <small class="user-detail">{{ usageDetailLine(item) }}</small>
                </div>
              </div>
            </td>
            <td class="total">{{ fmt.format(item.totalTokens) }}</td>
            <td>{{ isAggregateMode ? fmt.format(item.activeDays ?? 0) : fmt.format(cacheTokens(item)) }}</td>
            <td>
              <template v-if="isAggregateMode">{{ item.lastDate || "-" }}</template>
              <template v-else>{{ compact.format(item.inputTokens) }} / {{ compact.format(item.outputTokens) }}</template>
            </td>
            <td>{{ item.totalCost == null ? "-" : usd.format(item.totalCost) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="leaderboard-cards" :aria-label="isAggregateMode ? `移动端${totalHeader}` : `${date} 移动端日榜`">
      <article
        v-for="item in items"
        :key="`${item.id}-card`"
        class="lb-card"
        :class="{ mine: item.user?.id === user?.id }"
      >
        <div class="lb-card-top">
          <span class="lb-rank">{{ padRank(item.rank) }}</span>
          <a
            v-if="item.user?.profileUrl"
            class="profile-avatar-link"
            :href="item.user.profileUrl"
            :aria-label="`查看 ${item.user?.displayName || '小能熊用户'} 的社区主页`"
          >
            <img v-if="item.user?.avatarUrl" :src="item.user.avatarUrl" alt="" />
            <span v-else class="avatar-fallback">{{ (item.user?.displayName || "小").slice(0, 1) }}</span>
          </a>
          <template v-else>
            <img v-if="item.user?.avatarUrl" :src="item.user.avatarUrl" alt="" />
            <span v-else class="avatar-fallback">{{ (item.user?.displayName || "小").slice(0, 1) }}</span>
          </template>
          <div class="lb-user">
            <div class="user-name-line">
              <a v-if="item.user?.profileUrl" class="profile-name-link" :href="item.user.profileUrl">
                <strong>{{ item.user?.displayName || "小能熊用户" }}</strong>
              </a>
              <strong v-else>{{ item.user?.displayName || "小能熊用户" }}</strong>
              <span v-if="item.user?.id === user?.id" class="me-pill">我</span>
            </div>
            <MemberBadges :memberships="item.user?.memberships" compact />
            <small>{{ usageDetailLine(item) }}</small>
          </div>
          <div class="lb-total">
            <div class="lb-total-num">{{ compact.format(item.totalTokens) }}</div>
            <div class="lb-total-cost">{{ item.totalCost == null ? "-" : usd.format(item.totalCost) }}</div>
          </div>
        </div>
        <div class="lb-detail-line">
          <span v-if="isAggregateMode">活跃 {{ fmt.format(item.activeDays ?? 0) }} 天 · 最近 {{ item.lastDate || "-" }}</span>
          <span v-else>
            cache {{ compact.format(cacheTokens(item)) }} · input {{ compact.format(item.inputTokens) }} · output {{ compact.format(item.outputTokens) }}
          </span>
        </div>
      </article>
    </div>
  </template>
</template>
